import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useSearchParams } from 'react-router-dom'

const Referral: React.FC = () => {
  const [searchParams] = useSearchParams()
  const referrerName = searchParams.get('ref') || ''

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    referredBy: referrerName,
    projectType: 'brand-identity',
    message: '',
  })
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const apiKey = import.meta.env.VITE_WEB3FORMS_KEY
    if (apiKey) {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: apiKey,
          subject: `Referral from ${formData.referredBy || 'a client'}`,
          from_name: formData.name,
          ...formData,
        }),
      })
      if (response.ok) {
        setSubmitted(true)
      }
    } else {
      // Fallback when no API key is configured
      console.log('Referral form submitted:', formData)
      setSubmitted(true)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const highlights = [
    { stat: '100+', label: 'Projects Delivered' },
    { stat: '24hr', label: 'Response Time' },
    { stat: '5-Star', label: 'Client Reviews' },
  ]

  const services = [
    {
      title: 'Vehicle Wraps',
      description: 'Turn your fleet into mobile billboards with professional graphics designed for maximum impact.',
    },
    {
      title: 'Brand Identity',
      description: 'Complete brand systems from logo design to full guidelines that define who you are.',
    },
    {
      title: 'Web Design',
      description: 'Custom websites and digital experiences built for performance and conversions.',
    },
  ]

  return (
    <div className="min-h-screen bg-background text-primary">
      {/* Hero */}
      <section className="pt-40 pb-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            className="inline-block px-4 py-2 border border-accent/30 bg-accent/5 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-xs font-bold uppercase tracking-wider text-accent">
              {referrerName ? `Referred by ${referrerName}` : 'You were referred'}
            </span>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl lg:text-8xl font-display font-medium tracking-tighter text-white mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            Welcome to<br />
            <span className="text-neutral-500">chipahauk.</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-neutral-400 font-light leading-relaxed max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            Someone you trust thinks we'd be a great fit for your business.
            We're a creative solutions house that delivers high-caliber design with exceptional speed.
          </motion.p>

          <motion.a
            href="#referral-form"
            className="inline-flex items-center px-10 py-5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            Get started
          </motion.a>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6 border-t border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <motion.div
                key={item.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl md:text-5xl font-display font-medium text-white mb-2">{item.stat}</div>
                <div className="text-xs md:text-sm text-neutral-500 uppercase tracking-wider font-bold">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-4 text-white">What we do</h2>
            <p className="text-xl text-neutral-400 font-light">Business-focused creative services designed for impact.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-background p-12 hover:bg-surfaceHighlight/30 transition-colors duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-display font-medium text-white mb-4">{service.title}</h3>
                <p className="text-neutral-400 font-light leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Link
              to="/portfolio"
              className="inline-flex items-center text-sm text-neutral-400 hover:text-white transition-colors font-bold uppercase tracking-wider gap-2"
            >
              View our work
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Referral Form */}
      <section id="referral-form" className="py-32 px-6 border-t border-white/5 bg-surfaceHighlight/20">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-6 text-white">Let's connect</h2>
            <p className="text-xl text-neutral-400 font-light">
              Tell us a bit about yourself and what you're looking for. We'll be in touch within 24 hours.
            </p>
          </motion.div>

          {submitted ? (
            <motion.div
              className="text-center py-20 bg-background border border-white/5"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-5xl mb-6">
                <svg className="w-16 h-16 mx-auto text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-3xl font-display font-medium text-white mb-4">We got your info</h3>
              <p className="text-neutral-400 font-light text-lg max-w-md mx-auto">
                Thanks for reaching out. A member of our team will be in touch within 24 hours.
              </p>
            </motion.div>
          ) : (
            <motion.form
              onSubmit={handleSubmit}
              className="space-y-8 bg-background p-8 md:p-12 border border-white/5 shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Who Referred You?</label>
                  <input
                    type="text"
                    name="referredBy"
                    value={formData.referredBy}
                    onChange={handleChange}
                    className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                    placeholder="Name of person or company"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">What are you looking for?</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white text-lg appearance-none"
                >
                  <option value="brand-identity" className="bg-neutral-900">Brand & Identity</option>
                  <option value="web-design" className="bg-neutral-900">Website Design</option>
                  <option value="vehicle-graphics" className="bg-neutral-900">Vehicle Wraps & Graphics</option>
                  <option value="print-collateral" className="bg-neutral-900">Print & Collateral</option>
                  <option value="full-service" className="bg-neutral-900">Full Service Project</option>
                  <option value="not-sure" className="bg-neutral-900">Not Sure Yet</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Tell us about your project</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="What are your goals? Any timeline or budget in mind?"
                  className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors resize-none bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full px-8 py-5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
                >
                  Send inquiry
                </button>
              </div>
            </motion.form>
          )}
        </div>
      </section>
    </div>
  )
}

export default Referral
